import { Delay } from "@floating-ui/react";
import classNames from "classnames";
import isString from "lodash/isString";
import isUndefined from "lodash/isUndefined";
import { Label } from "PFComponents/label";
import { Tooltip } from "PFComponents/tooltip";
import React, { ReactNode, useCallback, useEffect, useState } from "react";

import { Tip } from "../text/input_field_set/components/tip";
import { Typography } from "../typography";
import css from "./multi_toggle.module.scss";
import { getTextVariant } from "./multi_toggle.utils";

export type Option<ID_TYPE extends string | number = string> = {
  id: ID_TYPE;
  value: string | number | React.ReactElement;
  disabled?: boolean;
  tooltipContent?: string | false;
  title?: string;
  ariaLabel?: string;
};

export type MultiToggleProps<ID_TYPE extends string | number> = React.AriaAttributes & {
  qaId?: string;
  label?: string | ReactNode;
  defaultSelected?: ID_TYPE;
  options: Option<ID_TYPE>[];
  controlledValue?: ID_TYPE | null;
  onChange: (id: ID_TYPE) => void;
  rounded?: boolean;
  fluid?: boolean;
  fitHeightToContent?: boolean;
  tip?: string;
  textVariant?: "body" | "label" | "labelSmall";
  tooltipDelay?: Delay;
  classes?: Partial<{
    root: string;
    container: string;
    button: string;
    buttonLabel: string;
  }>;
};

export const MultiToggle = <ID_TYPE extends string | number = string>({
  qaId,
  label,
  defaultSelected,
  options,
  controlledValue,
  onChange,
  rounded = false,
  fluid = false,
  fitHeightToContent = false,
  tip,
  textVariant = "body",
  tooltipDelay,
  classes = {}
}: MultiToggleProps<ID_TYPE>) => {
  const [selectedOption, setSelectedOption] = useState<ID_TYPE | undefined>(defaultSelected);

  const handleOnClick = useCallback(
    (id: ID_TYPE) => {
      setSelectedOption(id);
      onChange(id);
    },
    [onChange]
  );

  useEffect(() => {
    setSelectedOption(defaultSelected);
  }, [defaultSelected]);

  return (
    <div className={classNames(css.root, classes.root)} data-qa-id={qaId}>
      {label && (isString(label) ? <Label text={label} /> : label)}
      <div
        role="group"
        className={classNames(
          css.multiToggleContainer,
          { [css.rounded]: rounded, [css.fluid]: fluid, [css.fitHeightToContent]: fitHeightToContent },
          classes.container
        )}
      >
        {options.map(({ id, disabled, tooltipContent, value, title, ariaLabel }) => {
          const fieldValue = isUndefined(controlledValue) ? selectedOption : controlledValue;
          const isSelectedOption = fieldValue === id;
          const tooltipTitle = title || (typeof value === "string" ? value : "");
          return (
            <Tooltip<HTMLButtonElement>
              key={id}
              content={tooltipContent}
              disabled={!tooltipContent}
              delay={tooltipDelay}
            >
              {({ ref, getReferenceProps }) => (
                <button
                  ref={ref}
                  data-qa-id={`MultiToggleOption-${String(id)}`}
                  {...getReferenceProps({
                    "aria-pressed": isSelectedOption,
                    "aria-label": ariaLabel,
                    "type": "button",
                    "className": classNames(
                      css.button,
                      {
                        [css.selected]: isSelectedOption,
                        [css.disabled]: disabled
                      },
                      classes.button
                    ),
                    "onClick": () => !disabled && handleOnClick(id),
                    disabled
                  })}
                >
                  <Typography
                    variant={getTextVariant(isSelectedOption, textVariant)}
                    tag="span"
                    className={classNames(css.buttonLabel, classes.buttonLabel)}
                    textOverflow="clip"
                    title={tooltipTitle}
                  >
                    {value}
                  </Typography>
                </button>
              )}
            </Tooltip>
          );
        })}
      </div>
      {tip && <Tip tip={tip} />}
    </div>
  );
};
